import { useEffect, useRef, useSyncExternalStore } from 'react'

/*
 * WHICH TABS A SHEET IS SHOWING, AND THE LAST PICTURE IT HAD OF EACH — for the corner of a
 * session (AgentTabCorner). While a sheet pictures a tab (TabShotSheet, opened from More or
 * from the corner) the corner takes none of that tab: the sheet takes its own at the same
 * moments, and two would be two turns of the tab and two of the account's thirty pictures a
 * minute (the review of 2026-10-15). When the sheet closes, the corner shows the sheet's last
 * picture instead of taking another. Memory only, a few at most.
 */
export type LeftPicture = { image: { mime: 'image/jpeg' | 'image/png'; base64: string }; at: number }

const showing = new Map<string, number>()
const left = new Map<string, LeftPicture>()
const LEFT_MAX = 4
const listeners = new Set<() => void>()
const emit = () => listeners.forEach((fn) => fn())
const subscribe = (fn: () => void) => {
  listeners.add(fn)
  return () => {
    listeners.delete(fn)
  }
}

/** A tab, by its browser and its label. */
export const tabRef = (browserId: string, tab: string) => `${browserId}|${tab}`

/**
 * Mounted by a sheet: this tab is shown, and on leaving its last picture is left for the corner.
 * A sheet moved to another of the agent's tabs leaves the one it moved from — with THAT tab's
 * picture: the render that moves it already holds the next tab's (none yet), and runs before
 * this tab's leaving does.
 */
export function useSheetShowing(id: string, last: { image?: LeftPicture['image'] | undefined; at: number }) {
  const lastOf = useRef(new Map<string, typeof last>())
  lastOf.current.set(id, last)
  useEffect(() => {
    const kept = lastOf.current // one Map for the sheet's life, written by every render
    showing.set(id, (showing.get(id) ?? 0) + 1)
    // What an earlier look left is older than this one: a look that ends before its picture
    // came must leave nothing, not that (the review of 2026-10-15, second round).
    left.delete(id)
    emit()
    return () => {
      const n = (showing.get(id) ?? 1) - 1
      if (n > 0) showing.set(id, n)
      else showing.delete(id)
      const { image, at } = kept.get(id) ?? { at: 0 }
      kept.delete(id)
      if (image) {
        left.delete(id)
        left.set(id, { image, at })
        for (const old of left.keys()) {
          if (left.size <= LEFT_MAX) break
          left.delete(old)
        }
      }
      emit()
    }
  }, [id])
}

/** Is a sheet showing this tab now? */
export function useSheetOn(id: string | null): boolean {
  return useSyncExternalStore(subscribe, () => (id ? (showing.get(id) ?? 0) > 0 : false))
}

/** The last picture a sheet had of this tab — given once. */
export function takeLeftPicture(id: string): LeftPicture | null {
  const p = left.get(id) ?? null
  left.delete(id)
  return p
}
